// `pipelines`: Swift `Pipeline` / `Stage` (GroundTeam/Models/Pipeline.swift). Stage ids are what `Record.stageId`,
// `VisitResult.movesToStageId` and `Autoplay.triggerStageId` point at, so they are kept exactly as received.
//
// Decoding template `Pipeline(name: "", stages: [Stage(name: "")])` (GroundTeam/Sync/ResilientDecoding.swift:118):
// each stored stage is merged over `Stage(name: "")`, so a stage missing a key takes that stage's default (a missing
// `id` becomes a fresh UUID, as on the phone), and a payload with no `stages` key reads as one unnamed stage.

import { RECORD_TYPES, type RecordType } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  BOOL,
  INT,
  STRING,
  UUID,
  arrayOf,
  createModel,
  decodeModel,
  enumOf,
  field,
  objectOf,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface StagePayload extends Passthrough {
  id: string;
  name: string;
  colorHex: string;
  /** Int. */
  order: number;
  /** Win probability 0–100 (Int) for the weighted pipeline. */
  probability: number;
  isWon: boolean;
  isLost: boolean;
}

export const STAGE_KEYS = [
  'id',
  'name',
  'colorHex',
  'order',
  'probability',
  'isWon',
  'isLost',
] as const satisfies readonly (keyof Known<StagePayload>)[];

export const DEFAULT_STAGE_COLOR = '#2673E6';
export const DEFAULT_STAGE_PROBABILITY = 10;

export const STAGE_FIELDS: FieldMap<StagePayload> = {
  id: field.req(UUID, newId),
  name: field.req(STRING, () => ''),
  colorHex: field.req(STRING, () => DEFAULT_STAGE_COLOR),
  order: field.req(INT, () => 0),
  probability: field.req(INT, () => DEFAULT_STAGE_PROBABILITY),
  isWon: field.req(BOOL, () => false),
  isLost: field.req(BOOL, () => false),
};

export const STAGE = objectOf<StagePayload>('Stage', STAGE_FIELDS);

/** Reads one stage as an element of `stages` (merged over the template `Stage(name: "")`). */
export function decodeStage(raw: JsonObject): Decoded<StagePayload> {
  return decodeModel(STAGE_FIELDS, raw, { hasId: false });
}

export type StageInit = Partial<Known<StagePayload>> & { name: string };

/** A full stage (every key; `order` and `probability` rounded to Int). */
export function newStage(init: StageInit): StagePayload {
  const defaults = {
    id: newId(),
    colorHex: DEFAULT_STAGE_COLOR,
    order: 0,
    probability: DEFAULT_STAGE_PROBABILITY,
    isWon: false,
    isLost: false,
  };
  return createModel(STAGE_FIELDS, defaults, init, 'stage');
}

export interface PipelinePayload extends Passthrough {
  id: string;
  name: string;
  recordType: RecordType;
  stages: StagePayload[];
  isDefault: boolean;
}

export const PIPELINE_KEYS = [
  'id',
  'name',
  'recordType',
  'stages',
  'isDefault',
] as const satisfies readonly (keyof Known<PipelinePayload>)[];

export const PIPELINE_FIELDS: FieldMap<PipelinePayload> = {
  id: field.strict(UUID),
  name: field.req(STRING, () => ''),
  recordType: field.req(enumOf('RecordType', RECORD_TYPES), () => 'lead'),
  stages: field.req(arrayOf(STAGE), () => [newStage({ name: '' })]),
  isDefault: field.req(BOOL, () => false),
};

export function decodePipeline(raw: JsonObject, envelopeId?: string): Decoded<PipelinePayload> {
  return decodeModel(PIPELINE_FIELDS, raw, {}, envelopeId);
}

export type PipelineInit = Omit<Partial<Known<PipelinePayload>>, 'stages'> & {
  name: string;
  /** Each stage is completed with the Stage defaults (`newStage`), so partial stages are fine. */
  stages?: StageInit[];
};

export function newPipeline(init: PipelineInit): PipelinePayload {
  const { stages, ...rest } = init;
  const defaults = {
    id: newId(),
    recordType: 'lead',
    stages: (stages ?? []).map((s) => newStage(s)),
    isDefault: false,
  };
  return createModel(PIPELINE_FIELDS, defaults, rest, 'pipelines');
}

/** `Pipeline.orderedStages`. */
export function orderedStages(p: Pick<PipelinePayload, 'stages'>): StagePayload[] {
  return [...p.stages].sort((a, b) => a.order - b.order);
}
